import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  lookupResidents,
  ResidentCandidate,
  ProvenanceSource,
  ResolutionState,
} from '../../api/residents-client';
import { palette, radius, spacing, shadow, transition, typography, status, zIndex } from '../../style-utils/theme';
import { Presence, m, overlayMotion } from '../../motion';

// ---------------------------------------------------------------------------
// PersonField — one component, every form that names a person.
// ---------------------------------------------------------------------------
//
// The ask, verbatim: "if I scan an ID and the AI gets the name wrong I need to
// fix it manually, and a package should give me a dropdown in case 2 tenants
// are named the same."
//
// That is TWO mechanisms, and this component is careful to keep them apart:
//
//   CORRECTION  the input is ALWAYS editable and the override is recorded.
//               Applies to every name, including a visitor's.
//   RESOLUTION  the dropdown that links a name to a specific resident.
//               Applies ONLY where the name refers to a resident — so
//               `resolves` is off for visitor-log guest names, who are
//               outsiders and must never be linked to an occupancy.
//
// ============ THE RULE THE UI MUST NOT SOFTEN ============
// 2+ current candidates -> a pick is REQUIRED. Not a warning, not a hint. The
// escape hatch is an explicit "None of these — not a resident", which is itself
// a recorded decision rather than a silent skip. The server enforces this
// independently (it returns 400 with the count) and the database enforces it
// again with a CHECK constraint, so softening it here only produces a form that
// looks like it saved and did not.
//
// ============ AUTOCAPTURE ============
// Most of the time there is nothing to ask: a unit with one resident resolves
// on the unit alone. When that happens the field shows a chip explaining WHY it
// filled itself in — a silently populated name field is unsettling; "Only
// resident of 4B" is reassuring — and stays fully overridable.

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
`;

const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

/* Provenance badge: Scanned / Scanned · edited / Manual. */
const Badge = styled.span<{ tone: 'brand' | 'warning' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  background: ${p => status[p.tone].bg};
  color: ${p => status[p.tone].fg};
`;

const Input = styled.input<{ invalid?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${p => (p.invalid ? palette.alert : palette.lineStrong)};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${p => (p.invalid ? shadow.focusError : shadow.focus)};
  }
`;

const Menu = m.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  transform-origin: top center;
  /* zIndex.dropdown, not a raw 40 (WP-18). No paint failure was observed
     here — these menus' hosts carry no sticky bar to lose to — so this is
     contract hygiene, not a repro. It matters anyway: 40 sits between no
     two tiers on the scale, so the next sticky element added above this
     host wins silently and the bug looks like a rendering mystery.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  z-index: ${zIndex.dropdown};
  margin-top: ${spacing.half};
  max-height: 264px;
  overflow-y: auto;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.md};
`;

const Row = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${spacing.xs};
  width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  border: none;
  background: ${p => (p.active ? palette.irisTintSoft : 'transparent')};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  text-align: left;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

/* The unit is not decoration — it is the only thing separating two candidates. */
const RowUnit = styled.span`
  color: ${palette.lagoon};
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
`;

const RowFormer = styled.span`
  color: ${palette.slateLight};
  font-size: ${typography.size.xxs}px;
  white-space: nowrap;
`;

const NoneRow = styled(Row)`
  border-top: 1px solid ${palette.line};
  color: ${palette.slate};
  font-style: italic;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  align-self: flex-start;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.irisTint};
  color: ${palette.tealDeep};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
`;

const ChipClear = styled.button`
  border: none;
  background: none;
  padding: 0 0 0 ${spacing.half};
  color: ${palette.lagoon};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  text-decoration: underline;
`;

const Hint = styled.p<{ tone?: 'alert' | 'muted' }>`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${p => (p.tone === 'alert' ? palette.alert : palette.slate)};
`;

export interface PersonFieldValue {
  occupancy_id: string | null;
  resolution: ResolutionState;
  /**
   * The chosen resident's unit label, when one was chosen. OPTIONAL and
   * additive — every existing consumer reads only the two fields above.
   *
   * It exists for the parcel handover, which is the one place that has a SECOND
   * unit to compare against (the one the parcel is addressed to) and therefore
   * the one place that can say "a resident of 7A collected a parcel for 4B".
   */
  unit_label?: string | null;
}

export interface PersonFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;

  /**
   * Does this name refer to a RESIDENT?
   *
   * false for visitor-log guest names: a visitor is an outsider, and offering
   * to link one to an occupancy would invite staff to assert something false.
   * They still get the provenance badge and the editable input.
   */
  resolves?: boolean;

  buildingId?: number | null;
  /** Narrowing by unit is what makes most lookups unambiguous. */
  unitId?: number | null;
  /**
   * What `unitId` MEANS for this field.
   *
   * 'unit'      (default) the unit FILTERS. "Who is it for" — the recipient
   *             lives in the unit the parcel is addressed to, the host lives in
   *             the unit being visited. Three of the four mounts want this and
   *             are untouched.
   * 'building'  the unit RANKS. "Who collected it" — a neighbour, a spouse from
   *             another unit or a resident's adult child collecting is
   *             legitimate, so the search covers the building and the entry's
   *             own unit is floated to the top.
   *
   * 'building' widens the search, and widening it is the whole point; it does
   * NOT widen what may be saved. More candidates means MORE fields that demand
   * an explicit pick, and the server and a CHECK constraint enforce that
   * independently of anything here.
   */
  unitScope?: 'unit' | 'building';

  resolution?: ResolutionState;
  occupancyId?: string | null;
  onResolve?: (next: PersonFieldValue) => void;

  /** Provenance for THIS field, if a scanner proposed the value. */
  source?: ProvenanceSource;
  scanRaw?: string;

  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  /** Server-reported ambiguity, so a rejected save reopens the picker here. */
  serverCandidates?: ResidentCandidate[];
  /**
   * Fires when this field starts or stops REQUIRING a pick, so the parent can
   * disable Next/Save.
   *
   * The parent cannot work this out for itself: `resolution === 'unset'` is
   * also the state when NOTHING matched, and treating that as blocking would
   * strand the form on any name the building does not contain. Only this
   * component knows the candidate count, so only it can answer.
   */
  onRequirePickChange?: (required: boolean) => void;
}

// NOTE: the "Scan ID" button is deliberately NOT rendered here.
//
// One scan fills BOTH halves of a name, so the button belongs to the PAIR of
// fields, not to either one of them. Parents render <ScanIdButton> themselves,
// centred beneath the pair — see name-and-last-name.tsx. Hanging it off the
// given-name field made it look like it only applied to that field, and left
// the surname looking like it had no scan at all.

// How much has to be typed before unitScope='building' actually goes
// building-wide. See the note in runLookup: this is the PII-surface answer, not
// a performance one — the roster is cached and a keystroke costs a map lookup.
const BUILDING_WIDE_MIN_CHARS = 2;

const BADGE: Record<ProvenanceSource, { text: string; tone: 'brand' | 'warning' | 'neutral' }> = {
  scan: { text: 'Scanned', tone: 'brand' },
  scan_edited: { text: 'Scanned · edited', tone: 'warning' },
  manual: { text: 'Manual', tone: 'neutral' },
};

export const PersonField: React.FC<PersonFieldProps> = ({
  label,
  value,
  onChange,
  resolves = false,
  buildingId,
  unitId,
  unitScope = 'unit',
  resolution = 'unset',
  occupancyId = null,
  onResolve,
  source,
  scanRaw,
  placeholder,
  maxLength = 60,
  disabled,
  serverCandidates,
  onRequirePickChange,
}) => {
  const [candidates, setCandidates] = useState<ResidentCandidate[]>([]);
  const [currentCount, setCurrentCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Guards against a slow response for an older query overwriting a newer one.
  const seq = useRef(0);

  // A scanned value the human has since changed is 'scan_edited'. Derived here
  // rather than stored, so it cannot go stale; the server derives the same
  // transition independently and its answer is the one that persists.
  const effectiveSource: ProvenanceSource | undefined =
    source === 'scan' && scanRaw !== undefined && scanRaw.trim() !== value.trim()
      ? 'scan_edited'
      : source;

  // Server-reported ambiguity wins: a save was just refused, and these are the
  // exact rows it refused over.
  useEffect(() => {
    if (serverCandidates && serverCandidates.length) {
      setCandidates(serverCandidates);
      setCurrentCount(
        serverCandidates.filter(c => c.occupancy_status === 'current').length
      );
      setOpen(true);
    }
  }, [serverCandidates]);

  const runLookup = useCallback(
    async (q: string) => {
      if (!resolves || !buildingId) return;
      const mine = ++seq.current;
      setLoading(true);
      try {
        // TWO TYPED CHARACTERS BEFORE THE BUILDING OPENS UP. This is a PII
        // enumeration surface, and a blank query building-wide would list 25 of
        // a stranger's neighbours the moment the field takes focus. Below the
        // threshold the entry's own unit still FILTERS, so the field opens on
        // the small, obviously-relevant set and the spouse case stays one tap;
        // the wider list only appears once the desk has actually typed a name.
        const wide =
          unitScope === 'building' && q.trim().length >= BUILDING_WIDE_MIN_CHARS;
        const res = await lookupResidents({
          buildingId,
          q,
          unitId: wide ? null : unitId,
          preferUnitId: wide ? unitId : null,
        });
        if (mine !== seq.current) return; // a newer keystroke already answered
        setCandidates(res.residents);
        setCurrentCount(res.current_count);
      } catch {
        // A failed lookup must not block the entry. The field stays editable
        // and the server still enforces resolution on save, so the worst case
        // is the concierge being asked to pick on the next attempt rather than
        // being stuck on a form that will not submit.
        if (mine === seq.current) {
          setCandidates([]);
          setCurrentCount(0);
        }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [resolves, buildingId, unitId, unitScope]
  );

  // 200 ms debounce — the roster is cached server-side for 30 s, so a keystroke
  // costs a map lookup, but there is no reason to spend a request per character.
  useEffect(() => {
    if (!resolves || !buildingId) return;
    const t = setTimeout(() => runLookup(value), 200);

    return () => clearTimeout(t);
  }, [value, resolves, buildingId, unitId, unitScope, runLookup]);

  // AUTOCAPTURE, mirrored client-side so the chip appears as the user types
  // rather than only after a save. Exactly ONE current candidate and no
  // decision yet -> link it and say so. Never fires at 2+: that is the case a
  // human must decide.
  useEffect(() => {
    if (!resolves || !onResolve) return;
    if (resolution !== 'unset') return; // a human already decided
    // NEVER LATCH A LINK MADE AGAINST THE NARROW SET.
    //
    // Under 'building' scope the first two characters are still looked up
    // unit-filtered, and a machine link made there would SURVIVE into the wide
    // set: this effect stops re-running once `resolution` leaves 'unset', and
    // mustPick below only ever inspects 'unset'. So a name that is unique in
    // the unit but shared by two residents of the building would sail past the
    // 2+ rule. The unit's residents are still listed and still one tap — what
    // is withheld here is the SILENT link, not the choice.
    if (
      unitScope === 'building' &&
      value.trim().length < BUILDING_WIDE_MIN_CHARS
    ) {
      return;
    }
    const currents = candidates.filter(c => c.occupancy_status === 'current');
    if (currents.length === 1 && value.trim()) {
      onResolve({
        occupancy_id: currents[0].occupancy_id,
        resolution: 'single_match',
        unit_label: currents[0].unit_label,
      });
    }
  }, [candidates, resolution, resolves, onResolve, value, unitScope]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);

    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const chosen = candidates.find(c => c.occupancy_id === occupancyId);
  // 2+ CURRENT candidates and nobody has chosen. Former residents are shown in
  // the list but never create this state — a unit that has ever turned over
  // would otherwise be permanently ambiguous.
  //
  // AN EMPTY FIELD IS NOT AMBIGUOUS, it is empty. Without the value guard a
  // blank box paints itself red and blocks the form in any building that
  // happens to hold two same-named residents — which building-wide scope makes
  // routine. This softens nothing: the client autocapture above already refuses
  // to act on a blank value, every parent's submit button is independently
  // disabled while the name is blank, and a blank name never reaches the
  // server's 2+ rule because it is rejected before it as a missing value.
  /* One id per mounted field, derived from the label so it stays readable in
     the DOM, plus React's own useId so three name boxes for one guest — or
     the same field remounted for another slot — can never collide. */
  const generatedId = React.useId();
  const fieldId = React.useMemo(
    () =>
      `person-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}${generatedId}`,
    [label, generatedId]
  );

  const mustPick =
    resolves && resolution === 'unset' && currentCount > 1 && value.trim() !== '';

  // Tell the parent, so Next/Save can be disabled while this is true. Zero
  // candidates is NOT blocking — that is an ordinary non-resident, and the
  // dropdown's explicit "None of these" is how it gets recorded.
  useEffect(() => {
    onRequirePickChange?.(mustPick);
  }, [mustPick, onRequirePickChange]);

  const pick = (c: ResidentCandidate) => {
    onChange(c.display_name);
    onResolve?.({
      occupancy_id: c.occupancy_id,
      resolution: 'picked',
      unit_label: c.unit_label,
    });
    setOpen(false);
  };

  const pickNone = () => {
    onResolve?.({ occupancy_id: null, resolution: 'no_match', unit_label: null });
    setOpen(false);
  };

  const clearChoice = () => {
    onResolve?.({ occupancy_id: null, resolution: 'unset', unit_label: null });
    setOpen(true);
  };

  return (
    <Wrap ref={wrapRef}>
      <LabelRow>
        {/* htmlFor + id. This label was a sibling of its input with no
            association at all, on EVERY name field in the product — "Name",
            "Middle Name (optional)", "Last Name", "Released to". A screen
            reader announced an unlabelled edit box, and clicking the label did
            not focus the field. */}
        <Label htmlFor={fieldId}>{label}</Label>
        {effectiveSource && (
          <Badge tone={BADGE[effectiveSource].tone}>{BADGE[effectiveSource].text}</Badge>
        )}
      </LabelRow>

      {/* ALWAYS editable. Correction is never blocked — that is the whole
          first half of the request. */}
      <Input
        id={fieldId}
        type="text"
        value={value}
        maxLength={maxLength}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        disabled={disabled}
        invalid={mustPick}
        /* The blocking state was communicated by colour alone: the box turned
           coral and nothing was announced, so a screen-reader user was refused
           at Next with no idea which field objected or why. */
        aria-invalid={mustPick || undefined}
        aria-describedby={mustPick ? `${fieldId}-ambiguity` : undefined}
        onChange={e => onChange(e.target.value)}
        onFocus={() => resolves && setOpen(true)}
        autoComplete="off"
      />

      {resolves && resolution === 'single_match' && chosen && (
        <Chip>
          {`${chosen.display_name} — Unit ${chosen.unit_label || '—'}`}
          {chosen.occupancy_status === 'moved_out' && ' · moved out'}
          <ChipClear type="button" onClick={clearChoice}>
            change
          </ChipClear>
        </Chip>
      )}

      {resolves && resolution === 'picked' && chosen && (
        <Chip>
          {`${chosen.display_name} — Unit ${chosen.unit_label || '—'}`}
          <ChipClear type="button" onClick={clearChoice}>
            change
          </ChipClear>
        </Chip>
      )}

      {resolves && resolution === 'no_match' && (
        <Chip>
          Not a resident
          <ChipClear type="button" onClick={clearChoice}>
            change
          </ChipClear>
        </Chip>
      )}

      {mustPick && (
        <Hint id={`${fieldId}-ambiguity`} tone="alert">
          {`${currentCount} residents match that name. Choose which one, or "None of these".`}
        </Hint>
      )}
      {resolves && loading && !mustPick && <Hint>Searching residents…</Hint>}

      <Presence>
        {resolves && open && (candidates.length > 0 || value.trim() !== '') ? (
        <Menu key="person-options" {...overlayMotion('popover')} role="listbox">
          {candidates.map(c => (
            <Row
              key={c.occupancy_id}
              type="button"
              /* role + aria-selected: the parent declares itself a listbox, so
                 its children have to BE options — a listbox of buttons is
                 invalid ARIA and announces as an empty one. `active` was
                 painting the selected state and telling assistive tech
                 nothing. */
              role="option"
              aria-selected={c.occupancy_id === occupancyId}
              active={c.occupancy_id === occupancyId}
              onClick={() => pick(c)}
            >
              <span>{c.display_name}</span>
              {/* THE TIEBREAKER. Two residents named Maria Silva are separated
                  by this and nothing else. */}
              <RowUnit>{`— Unit ${c.unit_label || '—'}`}</RowUnit>
              {c.occupancy_status === 'moved_out' && (
                <RowFormer>· moved out</RowFormer>
              )}
            </Row>
          ))}
          {/* Shown even with zero candidates: recording "not a resident" is a
              decision the desk must be able to make explicitly. */}
          <NoneRow type="button" onClick={pickNone}>
            None of these — not a resident
          </NoneRow>
        </Menu>
        ) : null}
      </Presence>
    </Wrap>
  );
};
